import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, Columns3, BarChart3, LayoutGrid, CalendarDays } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Pagination } from '../../../shared/components/Pagination';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { useOportunidades, useEtapas } from '../hooks';
import { useOportunidadeFilters } from '../hooks/useOportunidadeFilters';
import type {
  ListOportunidadesParams,
  OportunidadeStatus,
  Prioridade,
} from '../api';
import {
  ConfidencialBadge,
  OportunidadePrioridadeBadge,
  OportunidadeStatusBadge,
  StalledBadge,
} from '../components/OportunidadeBadges';
import { formatCurrencyBRL, formatDateBR } from '../utils/format';

const DEFAULT_PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: OportunidadeStatus; label: string }[] = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'ganha', label: 'Ganha' },
  { value: 'perdida', label: 'Perdida' },
  { value: 'suspensa', label: 'Suspensa' },
  { value: 'cancelada', label: 'Cancelada' },
];

const PRIORIDADE_OPTIONS: { value: Prioridade; label: string }[] = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

/**
 * Página raiz de Oportunidades Comerciais (US-COM-004 grupo 14 —
 * Fatia 9 Frontend, visão Lista).
 *
 * MVP: tabela com busca debounced, filtros por status/prioridade/etapa,
 * paginação server-side e navegação para a página de detalhe. Visões
 * Cards/Kanban/Calendário e o formulário de criação entram nas fatias
 * seguintes.
 */
export default function OportunidadesPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { filters, setFilter } = useOportunidadeFilters();
  // Preserva filtros ao alternar entre visões — a query string carrega
  // status/prioridade/stageId. Kanban não usa filtros compartilhados,
  // então não recebe `location.search`.
  const preservedSearch = location.search;
  // Busca vive local com debounce (o hook não redigita a URL a cada
  // tecla). Após 300ms sem digitar, propaga para o hook.
  const [searchTerm, setSearchTerm] = useState(filters.search ?? '');
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if ((filters.search ?? '') !== trimmed) setFilter('search', trimmed || undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, filters.search, setFilter]);

  // Reset paginação quando qualquer filtro muda.
  useEffect(() => {
    setOffset(0);
  }, [filters.search, filters.status, filters.prioridade, filters.stageId]);

  const params = useMemo<ListOportunidadesParams>(
    () => ({
      search: filters.search,
      status: filters.status,
      prioridade: filters.prioridade,
      stageId: filters.stageId,
      responsavelId: filters.responsavelId,
      clientId: filters.clientId,
      limit,
      offset,
    }),
    [filters, limit, offset],
  );

  const { data, isLoading, isError, error } = useOportunidades(params);
  const { data: stages } = useEtapas();

  const items = data?.data ?? [];
  const count = data?.pagination?.count ?? 0;

  const stageById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stages ?? []) map.set(s.id, s.label);
    return map;
  }, [stages]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Oportunidades Comerciais</h1>
          <p className="mt-1 text-sm text-gray-600">
            Pipeline de vendas — pré-vendas, propostas e desfechos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/oportunidades/indicadores')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            Indicadores
          </button>
          <button
            onClick={() => navigate(`/oportunidades/cards${preservedSearch}`)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <LayoutGrid className="h-4 w-4" />
            Cards
          </button>
          <button
            onClick={() => navigate('/oportunidades/kanban')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Columns3 className="h-4 w-4" />
            Kanban
          </button>
          <button
            onClick={() => navigate('/oportunidades/calendario')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <CalendarDays className="h-4 w-4" />
            Calendário
          </button>
          <button
            onClick={() => navigate('/oportunidades/nova')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nova oportunidade
          </button>
        </div>
      </div>

      {/* Filtros — inline, sem card wrapper (padrão UsersPage) */}
      <div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <TextInput
              label="Buscar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search className="h-4 w-4 text-gray-400" />}
              aria-label="Buscar oportunidades por código, nome ou produto"
            />
          </div>
          <AutocompleteSelect
            label="Status"
            options={[
              { value: '', label: 'Todos os status' },
              ...STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            ]}
            value={filters.status ?? ''}
            onChange={(v) => setFilter('status', v || undefined)}
          />
          <AutocompleteSelect
            label="Prioridade"
            options={[
              { value: '', label: 'Todas as prioridades' },
              ...PRIORIDADE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            ]}
            value={filters.prioridade ?? ''}
            onChange={(v) => setFilter('prioridade', v || undefined)}
          />
          <div className="md:col-span-4">
            <div className="md:w-1/3">
              <AutocompleteSelect
                label="Etapa"
                options={[
                  { value: '', label: 'Todas as etapas' },
                  ...((stages ?? []).filter((s) => s.active).map((s) => ({ value: s.id, label: s.label }))),
                ]}
                value={filters.stageId ?? ''}
                onChange={(v) => setFilter('stageId', v || undefined)}
              />
            </div>
          </div>
        </div>
      </div>

      {isError && (
        <Alert variant="error">
          Erro ao carregar oportunidades: {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {/* Tabela — raw, sem card wrapper (padrão UsersPage) */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Código</Th>
                <Th>Nome</Th>
                <Th>Etapa</Th>
                <Th>Status</Th>
                <Th>Prioridade</Th>
                <Th className="text-right">Valor Estimado</Th>
                <Th className="text-right">Valor Ponderado</Th>
                <Th>Fechamento Previsto</Th>
                <Th>Sinais</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-6 py-4">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-8">
                    <EmptyState
                      title="Nenhuma oportunidade encontrada"
                      description="Ajuste os filtros ou cadastre uma nova oportunidade."
                      icon={<Filter className="h-8 w-8" />}
                    />
                  </td>
                </tr>
              )}
              {!isLoading &&
                items.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/oportunidades/${o.id}`)}
                  >
                    <Td className="font-mono text-xs text-gray-700">
                      {o.codigo ?? <span className="italic text-gray-400">rascunho</span>}
                    </Td>
                    <Td className="font-medium text-gray-900">{o.nome}</Td>
                    <Td>{stageById.get(o.stageId) ?? '—'}</Td>
                    <Td>
                      <OportunidadeStatusBadge status={o.status} />
                    </Td>
                    <Td>
                      <OportunidadePrioridadeBadge prioridade={o.prioridade} />
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatCurrencyBRL(o.valorEstimado)}
                    </Td>
                    <Td className="text-right tabular-nums text-gray-600">
                      {formatCurrencyBRL(o.valorPonderado)}
                    </Td>
                    <Td>{formatDateBR(o.expectedClosingDate)}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {o.isConfidential && <ConfidencialBadge />}
                        {o.isStalled && <StalledBadge />}
                      </div>
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
      </div>

      <div>
        <Pagination
          offset={offset}
          limit={limit}
          count={count}
          itemLabel="oportunidade"
          itemLabelPlural="oportunidades"
          onChange={(next) => {
            setLimit(next.limit);
            setOffset(next.offset);
          }}
        />
      </div>
    </div>
  );
}

const Th: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <th
    scope="col"
    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 ${className ?? ''}`}
  >
    {children}
  </th>
);

const Td: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <td className={`px-6 py-4 text-sm ${className ?? ''}`}>{children}</td>
);
